import { useState } from "react";
import { ref, push, set } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";

const CREATIVE_ROLES = [
  "Video Editor",
  "Photographer",
  "Graphic Designer",
  "Motion Graphics Designer",
  "UX/UI Designer",
  "Social Media Manager",
  "Content Creator",
  "Copywriter",
  "Videographer",
  "Web Developer",
  "Brand Strategist",
  "Illustrator",
  "3D Artist",
  "Music Producer",
  "Voice Actor",
];

const INPUT_CLASS =
  "w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0 text-sm";

const PostJob = () => {
  const { currentUser } = useUser();
  const navigate = useMoveTo();

  const [form, setForm] = useState({
    title: "",
    role: "",
    companyName: "",
    budget: "",
    location: "",
    timeline: "",
    type: "freelance",
  });
  const [submitting, setSubmitting] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);

  const set_ = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const isValid =
    form.title.trim() &&
    form.role &&
    form.budget.trim() &&
    form.location.trim() &&
    form.timeline.trim();

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      const newRef = push(ref(database, "Jobs"));
      await set(newRef, {
        title: form.title.trim(),
        role: form.role,
        companyName: form.companyName.trim() || currentUser.name,
        budget: form.budget.trim(),
        payRate: form.budget.trim(),
        location: form.location.trim(),
        timeline: form.timeline.trim(),
        type: form.type,
        hostId: currentUser.uid,
        hostName: currentUser.name,
        hostProfilePic: currentUser.profilePic || "",
        status: "active",
        createdAt: Date.now(),
      });
      window.showToast("Job posted successfully!", "success");
      navigate(ROUTES.JOBS_MY_POSTS);
    } catch (e) {
      console.error("PostJob error:", e);
      window.showToast("Failed to post job. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
      <MobileHeader title="Post a Job" isBackBtn />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto pb-24 md:pb-5 px-4 pt-5 bg-white dark:bg-[#1A1A1A]">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex-shrink-0 md:block hidden"
            >
              <img
                src={ICONS.arrowBack}
                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                alt="Back"
              />
            </button>
            <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
              Post a Job
            </h2>
            <div className="w-5" />
          </div>

          <div className="max-w-lg mx-auto space-y-5">
            {/* Job type toggle */}
            <div>
              <label className="text-sm font-medium mb-2 block">Job Type</label>
              <div className="flex gap-3">
                {[
                  { key: "freelance", label: "Freelance" },
                  { key: "business", label: "Business Request" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => set_("type", t.key)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                      form.type === t.key
                        ? "bg-[#327691] text-white border-[#327691]"
                        : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Project title */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Project Title *
              </label>
              <input
                value={form.title}
                onChange={(e) => set_("title", e.target.value)}
                placeholder="e.g. Brand Video for New Product Launch"
                className={INPUT_CLASS}
              />
            </div>

            {/* Creative role */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Creative Role *
              </label>
              <div className="select-none">
                <button
                  type="button"
                  className={`w-full flex items-center justify-between ${roleDropdownOpen ? "rounded-t-xl font-semibold text-sm" : "rounded-xl"} px-4 py-3 outline-none bg-[#F5F9FE] dark:bg-white text-[#7C8BA0] border border-gray-200 dark:border-0`}
                  onClick={() => setRoleDropdownOpen((o) => !o)}
                >
                  <span
                    className={
                      form.role
                        ? "text-black dark:text-black"
                        : "text-[#7C8BA0]"
                    }
                  >
                    {form.role || "Select a role..."}
                  </span>
                  <svg
                    width="24"
                    height="24"
                    fill="none"
                    stroke="#7C8BA0"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    className={
                      roleDropdownOpen ? "rotate-180 transition" : "transition"
                    }
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                <div
                  className={`overflow-y-auto transition-all duration-700 ${roleDropdownOpen ? "max-h-64 opacity-100" : "max-h-0 opacity-0"} bg-[#F5F9FE] dark:bg-white rounded-b-xl border border-gray-200 dark:border-0`}
                >
                  {CREATIVE_ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className="w-full text-left px-4 py-2 text-black dark:text-black font-semibold hover:bg-[#e3eaf3] border-b border-gray-200 last:border-b-0"
                      onClick={() => {
                        set_("role", r);
                        setRoleDropdownOpen(false);
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Company / Brand name */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Company / Brand Name
              </label>
              <input
                value={form.companyName}
                onChange={(e) => set_("companyName", e.target.value)}
                placeholder={
                  currentUser?.name
                    ? `${currentUser.name} (default)`
                    : "Your company or brand name"
                }
                className={INPUT_CLASS}
              />
            </div>

            {/* Budget */}
            <div>
              <label className="text-sm font-medium mb-2 block">Budget *</label>
              <input
                value={form.budget}
                onChange={(e) => set_("budget", e.target.value)}
                placeholder="e.g. $400/day or $2,000 total"
                className={INPUT_CLASS}
              />
            </div>

            {/* Location */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Location *
              </label>
              <input
                value={form.location}
                onChange={(e) => set_("location", e.target.value)}
                placeholder="e.g. Remote, New York, Lagos"
                className={INPUT_CLASS}
              />
            </div>

            {/* Timeline */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Project Timeline *
              </label>
              <input
                value={form.timeline}
                onChange={(e) => set_("timeline", e.target.value)}
                placeholder="e.g. 2 weeks, 1 month"
                className={INPUT_CLASS}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              className="w-full py-3.5 rounded-xl bg-[#327691] text-white font-semibold text-sm disabled:opacity-40 transition-all mt-2"
            >
              {submitting ? "Posting..." : "Post Job"}
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PostJob;
